import styled, { css } from "styled-components";

import { COLOR, TEXT_COLOR } from "@sellernote/ui/src/sds-v2/styles/colors";
import { setFontStyle } from "@sellernote/ui/src/sds-v2/styles/typography";
import { mobile } from "@sellernote/ui/src/styles/functions";

import { fadeUpKeyframes } from "../index.styles";
import { RequestServiceGuideUiType } from ".";

const sectionRequestServiceGuide = styled.section<{
  uiType: RequestServiceGuideUiType;

  isCustomStyle: boolean;
}>`
  // 공통 UI style
  display: flex;
  flex-direction: column;
  justify-content: center;

  > * {
    color: ${({ isCustomStyle }) =>
      isCustomStyle ? COLOR.wh : TEXT_COLOR.black_2};
  }

  em {
    ${setFontStyle("Body3")};
  }

  ${({ uiType }) => {
    if (uiType === "section") {
      return css`
        align-items: center;

        background: url("/assets/images/main/request-guide-section-bg.webp");
        background-position: center;
        background-size: cover;
        background-repeat: no-repeat;

        padding: 80px 0;

        ${mobile(css`
          align-items: flex-start;
          padding: 48px 16px;
        `)};

        > h3 {
          margin-bottom: 4px;

          ${mobile(css`
            margin-bottom: 16px;
          `)};
        }
      `;
    }

    // floating 형태의 UI style
    return css`
      position: absolute;
      top: 50px;
      right: calc(50% - 565px);

      width: 432px;
      padding: 24px;
      background-color: ${COLOR.bk_80};
      border-radius: 8px;

      animation-delay: 0.2s;
      opacity: 0;

      &.active {
        animation-name: ${fadeUpKeyframes};
        animation-duration: 0.8s;
        animation-timing-function: cubic-bezier(0.65, 0, 0.35, 1);
        animation-fill-mode: forwards;
      }

      > h3 {
        ${setFontStyle("SubHead1")};
      }

      > em {
        margin-top: 4px;
      }

      form {
        width: 100%;
      }

      .input-row-container {
        flex-wrap: wrap;

        .custom-landing-input-text-field {
          flex: 1 0 calc(50% - 8px);
        }
      }

      // mobile 에서는 모달만 띄우므로 pc 스타일이 보이지 않도록 처리
      ${mobile(css`
        display: none;
      `)};
    `;
  }};
`;

/* 랜딩 페이지에서만 쓰이는 form 내 input style 이라 디자인 시스템에 적용하지 않고 style 로 custom 적용. */
const inputAndTextAreaStyle = css`
  label {
    color: ${TEXT_COLOR.white_1};
  }

  input,
  textarea {
    background-color: transparent;
    border: 1px solid ${COLOR.grayScale_600};
    color: ${TEXT_COLOR.white_1};

    &:focus {
      border: 1px solid ${COLOR.grayScale_300};
    }

    &::placeholder {
      color: ${TEXT_COLOR.white_disabled};
    }
  }
`;

const selectButtonStyle = css`
  label {
    .title {
      color: ${TEXT_COLOR.white_2};
    }

    svg path {
      fill: ${COLOR.grayScale_400};
    }
  }

  &.selected {
    label .title {
      color: ${TEXT_COLOR.white_1};
    }

    svg path {
      fill: ${TEXT_COLOR.white_1};
    }
  }
`;

const requestGuideForm = styled.form<{
  isCustomStyle: boolean;
}>`
  display: flex;
  flex-direction: column;

  width: 840px;
  margin-top: 24px;

  ${({ isCustomStyle }) =>
    isCustomStyle
      ? mobile(css`
          margin-top: 32px;
        `)
      : mobile(css`
          margin-top: 16px;
        `)};

  ${mobile(css`
    width: 100%;
  `)};

  .input-row-container {
    display: flex;
    gap: 16px;
    align-items: flex-start;

    ${mobile(css`
      flex-direction: column;
    `)};

    .custom-landing-input-text-field {
      ${({ isCustomStyle }) => isCustomStyle && inputAndTextAreaStyle};
    }
  }

  .guide {
    margin-top: 24px;
    color: ${({ isCustomStyle }) =>
      isCustomStyle ? TEXT_COLOR.white_1 : TEXT_COLOR.black_2};
    ${setFontStyle("Body3")};
  }

  .required {
    ${setFontStyle("Body5")};
    margin-left: 4px;
  }

  .radio-button-row-container {
    margin-top: 12px;
    display: flex;
    gap: 24px;

    ${({ isCustomStyle }) =>
      !isCustomStyle &&
      css`
        flex-wrap: wrap;
        gap: 0;
      `};

    .custom-landing-radio-button {
      ${({ isCustomStyle }) =>
        isCustomStyle
          ? selectButtonStyle
          : css`
              margin-right: 24px;
              margin-bottom: 16px;
            `};
    }
  }

  .text-area-row-container {
    margin-top: 24px;
    ${({ isCustomStyle }) => isCustomStyle && inputAndTextAreaStyle};

    ${mobile(css`
      margin-top: 8px;
    `)};

    textarea {
      height: 112px;
    }
  }

  .checkbox-row-container {
    margin-top: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;

    .custom-landing-checkbox {
      ${({ isCustomStyle }) => isCustomStyle && selectButtonStyle};
    }

    .sub-checkbox {
      margin-left: 16px;

      .custom-checkbox-text-button {
        margin-left: 4px;
        color: ${({ isCustomStyle }) =>
          isCustomStyle ? TEXT_COLOR.white_2 : COLOR.grayScale_700};
      }
    }
  }

  > .button {
    align-self: center;
    margin-top: ${({ isCustomStyle }) => (isCustomStyle ? "24px" : "32px")};
    padding-bottom: ${({ isCustomStyle }) => (isCustomStyle ? "0" : "24px")};
  }
`;

// submit 버튼이 모달 구조상 form 외부에 있으므로, form 태그가 아닌 div 태그로 mark up
const registerForm = styled.div`
  width: 100%;
  height: 500px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  ${mobile(css`
    min-height: 410px;
    height: 50vh;
  `)};
`;

const termsTitle = styled.div`
  ${mobile(css`
    ${setFontStyle("SubHead2")};
  `)};

  > .required {
    ${setFontStyle("SubHead3")};
  }
`;

const termsContainer = styled.div`
  height: 70vh;
  overflow-y: scroll;

  ${mobile(css`
    height: 60vh;
  `)};
`;

const phoneContainer = styled.div<{
  countryCallingCodeSize: number;
}>`
  position: relative;

  // 전화번호 입력 필드 커스텀 스타일 > 국제전화 국가번호 표기 위해 padding 추가
  & > .custom-input-text-field .input-container input {
    padding-left: ${({ countryCallingCodeSize }) =>
      countryCallingCodeSize + 16}px;
  }
`;

export default {
  sectionRequestServiceGuide,
  requestGuideForm,
  registerForm,
  termsTitle,
  termsContainer,
  phoneContainer,
};
